import React, { useMemo } from 'react';
import {
  UserPlus,
  Key,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileText,
  ClipboardCheck,
  CheckSquare,
  Square,
  RefreshCw,
  Wrench,
  XOctagon,
  Clock,
  CircleDot,
  PlayCircle,
  Building2,
  Receipt,
  History,
  TrendingUp,
  GitBranch as GitBranchIcon,
  Hammer,
  FileWarning,
  UserMinus,
  MessageCircle,
  type LucideIcon,
} from 'lucide-react';
import { formatDateLong } from '../../utils/date';

export interface TimelineEvent {
  id: string;
  date: string;
  type: string;
  title: string;
  description: string;
  icon?: string;
  status?: string;
}

const ICON_MAP: Record<string, LucideIcon> = {
  'user-plus': UserPlus,
  key: Key,
  'check-circle': CheckCircle,
  'x-circle': XCircle,
  'alert-circle': AlertCircle,
  'file-text': FileText,
  'clipboard-check': ClipboardCheck,
  'check-square': CheckSquare,
  square: Square,
  'refresh-cw': RefreshCw,
  wrench: Wrench,
  'x-octagon': XOctagon,
  clock: Clock,
  'play-circle': PlayCircle,
  building: Building2,
  receipt: Receipt,
  history: History,
  'trending-up': TrendingUp,
  'git-branch': GitBranchIcon,
  hammer: Hammer,
  'file-warning': FileWarning,
  'user-minus': UserMinus,
  'message-circle': MessageCircle,
};

export function TimelineIcon({ name }: { name?: string }) {
  const Cmp = (name && ICON_MAP[name]) || CircleDot;
  return <Cmp className="w-4 h-4 sm:w-5 sm:h-5 shrink-0 text-white" strokeWidth={1.75} aria-hidden />;
}

function defaultDotClass(ev: TimelineEvent): string {
  switch (ev.type) {
    case 'tenant_created':
      return 'bg-blue-500 ring-blue-100';
    case 'portal_linked':
      return 'bg-sky-400 ring-sky-100';
    case 'payment': {
      if (ev.status === 'paid') return 'bg-emerald-500 ring-emerald-100';
      if (ev.status === 'partial') return 'bg-amber-400 ring-amber-100';
      return 'bg-red-500 ring-red-100';
    }
    case 'document':
      return 'bg-gray-400 ring-gray-100';
    case 'delivery_checklist':
      return 'bg-violet-500 ring-violet-100';
    case 'inspection': {
      if (ev.status === 'completed') return 'bg-emerald-500 ring-emerald-100';
      return 'bg-blue-500 ring-blue-100';
    }
    case 'renewal_request':
      return 'bg-cyan-500 ring-cyan-100';
    case 'maintenance_report':
      return 'bg-orange-500 ring-orange-100';
    case 'contract_cancelled':
      return 'bg-red-600 ring-red-100';
    case 'contract_expired':
      return 'bg-amber-800 ring-amber-100';
    default:
      return 'bg-gray-400 ring-gray-100';
  }
}

function resolveDotClass(
  ev: TimelineEvent,
  colorMap: Record<string, string> | undefined,
  resolveDotClassProp: ((e: TimelineEvent) => string | undefined) | undefined,
): string {
  const custom = resolveDotClassProp?.(ev);
  if (custom) return custom;
  const fromMap = colorMap?.[ev.type];
  if (fromMap) {
    const trimmed = fromMap.trim();
    if (trimmed.startsWith('bg-')) {
      return trimmed.includes('ring-') ? trimmed : `${trimmed} ring-gray-100`;
    }
    return trimmed;
  }
  return defaultDotClass(ev);
}

type GroupRow =
  | { kind: 'header'; key: string; label: string }
  | { kind: 'item'; key: string; event: TimelineEvent };

function groupByMonth(events: TimelineEvent[]): GroupRow[] {
  const rows: GroupRow[] = [];
  let lastKey = '';
  for (const ev of events) {
    const d = new Date(ev.date);
    const y = d.getFullYear();
    const m = d.getMonth();
    const mk = `${y}-${m}`;
    if (mk !== lastKey) {
      lastKey = mk;
      const label = d.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
      const capitalized = label.charAt(0).toUpperCase() + label.slice(1);
      rows.push({ kind: 'header', key: `h-${mk}`, label: capitalized });
    }
    rows.push({ kind: 'item', key: ev.id, event: ev });
  }
  return rows;
}

export type GenericTimelineProps = {
  events: TimelineEvent[];
  isLoading?: boolean;
  error?: Error | null;
  emptyMessage?: string;
  /** Clases Tailwind para el punto: `bg-*` y opcionalmente `ring-*` */
  colorMap?: Record<string, string>;
  /** Si devuelve string, sustituye colorMap y el default del tenant */
  resolveDotClass?: (ev: TimelineEvent) => string | undefined;
};

export function GenericTimeline({
  events,
  isLoading,
  error,
  emptyMessage = 'No hay eventos registrados.',
  colorMap,
  resolveDotClass: resolveDotClassProp,
}: GenericTimelineProps) {
  const grouped = useMemo(() => {
    if (events.length === 0) return [];
    return groupByMonth(events);
  }, [events]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 text-red-800 px-4 py-3 text-sm">
        {error.message || 'No se pudo cargar el historial.'}
      </div>
    );
  }

  if (events.length === 0) {
    return <p className="text-sm text-gray-500">{emptyMessage}</p>;
  }

  return (
    <div className="relative pl-2 sm:pl-0">
      <div className="absolute left-[11px] top-2 bottom-2 w-px bg-gray-200 sm:left-[15px]" aria-hidden />
      <ul className="space-y-0">
        {grouped.map((row) => {
          if (row.kind === 'header') {
            return (
              <li key={row.key} className="relative pt-6 first:pt-0">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 pl-10 sm:pl-12 mb-3">{row.label}</h3>
              </li>
            );
          }
          const ev = row.event;
          const dotClass = resolveDotClass(ev, colorMap, resolveDotClassProp);
          return (
            <li key={row.key} className="relative flex gap-4 pb-8 last:pb-0">
              <div className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center sm:h-9 sm:w-9">
                <span
                  className={`flex h-8 w-8 items-center justify-center rounded-full ring-4 ring-white ${dotClass} text-white shadow-sm sm:h-9 sm:w-9`}
                >
                  <TimelineIcon name={ev.icon} />
                </span>
              </div>
              <div className="min-w-0 flex-1 pt-0.5">
                <p className="text-xs text-gray-500 mb-1">{formatDateLong(ev.date)}</p>
                <p className="font-semibold text-gray-900">{ev.title}</p>
                <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap break-words">{ev.description}</p>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
